import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ComposedChart, Bar, Legend,
} from 'recharts';
import {
    FaExclamationCircle, FaCheckCircle, FaExclamationTriangle, FaTasks, FaUniversity,
} from 'react-icons/fa';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import { CustomerInvoiceRestDataProvider } from '../../../data/CustomerInvoiceRestDataProvider';
import { SupplierInvoiceRestDataProvider } from '../../../data/SupplierInvoiceRestDataProvider';
import { BankTransactionRestDataProvider } from '../../../data/BankTransactionRestDataProvider';
import { FiscalYearRestDataProvider } from '../../../data/FiscalYearRestDataProvider';
import { CustomerRestDataProvider } from '../../../data/CustomerRestDataProvider';
import { SupplierRestDataProvider } from '../../../data/SupplierRestDataProvider';
import { TaskItemRestDataProvider } from '../../../data/TaskItemRestDataProvider';
import { CustomerInvoice } from '../../../models/CustomerInvoice';
import { SupplierInvoice } from '../../../models/SupplierInvoice';
import { BankTransaction } from '../../../models/BankTransaction';
import { FiscalYear, FiscalYearState } from '../../../models/FiscalYear';
import { Customer } from '../../../models/Customer';
import { Supplier } from '../../../models/Supplier';
import { TaskItem, TaskPriority } from '../../../models/TaskItem';
import './HomeScreen.scss';
import '../../../styles/GenericSiteStyles.scss';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function findCurrentFiscalYear(fys: FiscalYear[]): FiscalYear | null {
    if (!fys.length) return null;
    const sorted = [...fys].sort(
        (a, b) => new Date(b.end).getTime() - new Date(a.end).getTime(),
    );
    return (
        sorted.find(f => f.state === FiscalYearState.Open) ??
        sorted.find(f => f.state === FiscalYearState.Closing) ??
        sorted[0]
    );
}

function monthKey(dateStr: string): string {
    return dateStr.slice(0, 7);
}

function monthSlots(fyStart: string, fyEnd: string): { key: string; label: string }[] {
    const result: { key: string; label: string }[] = [];
    const start = new Date(fyStart);
    const end = new Date(fyEnd);
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    const startYear = start.getFullYear();
    while (cur <= last) {
        const lbl = cur.toLocaleString('default', { month: 'short' });
        const yr = cur.getFullYear() !== startYear ? ` '${String(cur.getFullYear()).slice(2)}` : '';
        result.push({
            key: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`,
            label: lbl + yr,
        });
        cur.setMonth(cur.getMonth() + 1);
    }
    return result;
}

const formatCurrency = (val: number): string =>
    `€ ${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatCurrencyShort = (val: number): string => {
    if (Math.abs(val) >= 1_000_000) return `€${(val / 1_000_000).toFixed(1)}M`;
    if (Math.abs(val) >= 1_000) return `€${(val / 1_000).toFixed(1)}k`;
    return `€${val.toFixed(0)}`;
};

const daysOverdue = (today: Date, dueDateStr: string): number => {
    const due = new Date(dueDateStr);
    due.setHours(0, 0, 0, 0);
    return Math.max(0, Math.floor((today.getTime() - due.getTime()) / 86_400_000));
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="home-chart-tooltip">
            <p className="home-chart-tooltip__label">{label}</p>
            {payload.map((entry: any, i: number) => (
                <p key={i} className="home-chart-tooltip__item" style={{ color: entry.color }}>
                    <span className="home-chart-tooltip__name">{entry.name}:</span>{' '}
                    {formatCurrency(entry.value)}
                </p>
            ))}
        </div>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────

const HomeScreen: React.FC = () => {
    const { t } = useTranslation('screens');

    // ── State ──────────────────────────────────────────────────────────────────
    const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
    const [customerInvoices, setCustomerInvoices] = useState<CustomerInvoice[]>([]);
    const [supplierInvoices, setSupplierInvoices] = useState<SupplierInvoice[]>([]);
    const [bankTransactions, setBankTransactions] = useState<BankTransaction[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [taskItems, setTaskItems] = useState<TaskItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // ── Providers ──────────────────────────────────────────────────────────────
    const fyProvider = useMemo(() => new FiscalYearRestDataProvider(), []);
    const custInvProvider = useMemo(() => new CustomerInvoiceRestDataProvider(), []);
    const suppInvProvider = useMemo(() => new SupplierInvoiceRestDataProvider(), []);
    const bankTxProvider = useMemo(() => new BankTransactionRestDataProvider(), []);
    const custProvider = useMemo(() => new CustomerRestDataProvider(), []);
    const suppProvider = useMemo(() => new SupplierRestDataProvider(), []);
    const taskProvider = useMemo(() => new TaskItemRestDataProvider(), []);

    // ── Load ───────────────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [fys, cInv, sInv, txs, custs, supps, tasks] = await Promise.all([
                fyProvider.getAll(),
                custInvProvider.getAll(),
                suppInvProvider.getAll(),
                bankTxProvider.getAll(),
                custProvider.getAll(),
                suppProvider.getAll(),
                taskProvider.getAll(),
            ]);
            setFiscalYears(fys);
            setCustomerInvoices(cInv);
            setSupplierInvoices(sInv);
            setBankTransactions(txs);
            setCustomers(custs);
            setSuppliers(supps);
            setTaskItems(tasks);
        } catch {
            setError(t('home.loadError'));
        } finally {
            setLoading(false);
        }
    }, [fyProvider, custInvProvider, suppInvProvider, bankTxProvider, custProvider, suppProvider, taskProvider, t]);

    useEffect(() => { loadData(); }, [loadData]);

    // ── Computed ───────────────────────────────────────────────────────────────
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const currentFY = useMemo(() => findCurrentFiscalYear(fiscalYears), [fiscalYears]);

    const customerMap = useMemo(() => {
        const m: Record<string, string> = {};
        customers.forEach(c => { m[c.id] = c.name; });
        return m;
    }, [customers]);

    const supplierMap = useMemo(() => {
        const m: Record<string, string> = {};
        suppliers.forEach(s => { m[s.id] = s.name; });
        return m;
    }, [suppliers]);

    const revenueChartData = useMemo(() => {
        if (!currentFY) return [];
        const slots = monthSlots(currentFY.start, currentFY.end);
        const map: Record<string, number> = {};
        slots.forEach(s => { map[s.key] = 0; });
        customerInvoices
            .filter(inv => inv.fiscalYearId === currentFY.id && inv.invoiceDate)
            .forEach(inv => {
                const k = monthKey(inv.invoiceDate!);
                if (k in map) map[k] += inv.totalAmountExVat;
            });
        let cum = 0;
        return slots.map(s => {
            const monthly = map[s.key];
            cum += monthly;
            return { month: s.label, monthly: +monthly.toFixed(2), cumulative: +cum.toFixed(2) };
        });
    }, [currentFY, customerInvoices]);

    const bankBalanceChartData = useMemo(() => {
        if (!currentFY) return [];
        const slots = monthSlots(currentFY.start, currentFY.end);

        const txSign = (indicator: string | null) => {
            const ind = (indicator ?? '').toUpperCase();
            return ind === 'C' || ind === 'RD' ? 1 : -1;
        };

        // Opening balance: sum of ALL transactions that settled before the FY start
        const openingBalance = bankTransactions
            .filter(tx => tx.valueDate < currentFY.start)
            .reduce((sum, tx) => sum + txSign(tx.creditDebitIndicator) * tx.amount, 0);

        // Monthly net movements within the FY
        const map: Record<string, number> = {};
        slots.forEach(s => { map[s.key] = 0; });
        bankTransactions
            .filter(tx => tx.valueDate >= currentFY.start && tx.valueDate <= currentFY.end)
            .forEach(tx => {
                const k = monthKey(tx.valueDate);
                if (!(k in map)) return;
                map[k] += txSign(tx.creditDebitIndicator) * tx.amount;
            });

        let bal = openingBalance;
        return slots.map(s => {
            bal += map[s.key];
            return { month: s.label, balance: +bal.toFixed(2) };
        });
    }, [currentFY, bankTransactions]);

    const overdueCustomers = useMemo(() =>
        customerInvoices
            .filter(inv => !inv.isPaid && inv.paymentDueDate && new Date(inv.paymentDueDate) < today)
            .sort((a, b) => new Date(a.paymentDueDate!).getTime() - new Date(b.paymentDueDate!).getTime()),
        [customerInvoices, today],
    );

    const overdueSuppliers = useMemo(() =>
        supplierInvoices
            .filter(inv => !inv.isPaid && inv.paymentDueDate && new Date(inv.paymentDueDate) < today)
            .sort((a, b) => new Date(a.paymentDueDate!).getTime() - new Date(b.paymentDueDate!).getTime()),
        [supplierInvoices, today],
    );

    const overdueCustTotal = useMemo(() =>
        overdueCustomers.reduce((s, inv) => s + inv.totalAmountIncVat, 0),
        [overdueCustomers],
    );

    const overdueSupplTotal = useMemo(() =>
        overdueSuppliers.reduce((s, inv) => s + inv.totalAmountIncVat, 0),
        [overdueSuppliers],
    );

    // ── Upcoming tasks: incomplete, sorted by dueDate asc (null last), then createdAt ──
    const upcomingTasks = useMemo(() =>
        taskItems
            .filter(t => !t.isCompleted)
            .sort((a, b) => {
                if (!a.dueDate && !b.dueDate) return 0;
                if (!a.dueDate) return 1;
                if (!b.dueDate) return -1;
                return a.dueDate.localeCompare(b.dueDate);
            }),
        [taskItems],
    );

    // ── Unaccounted bank transactions: no ledger ref AND not payment-matched ──
    const unaccountedTransactions = useMemo(() =>
        bankTransactions
            .filter(tx => !tx.ledgerReferenceCode && !tx.isPaymentMatched && !tx.isSupplierPaymentMatched)
            .sort((a, b) => a.valueDate.localeCompare(b.valueDate)),
        [bankTransactions],
    );

    const unaccountedTotal = useMemo(() =>
        unaccountedTransactions.reduce((sum, tx) => {
            const ind = (tx.creditDebitIndicator ?? '').toUpperCase();
            const sign = ind === 'C' || ind === 'RD' ? 1 : -1;
            return sum + sign * tx.amount;
        }, 0),
        [unaccountedTransactions],
    );

    // ── Render helpers ─────────────────────────────────────────────────────────

    const renderOverdueTable = (
        items: (CustomerInvoice | SupplierInvoice)[],
        nameMap: Record<string, string>,
        nameKeyFn: (item: any) => string,
    ) => {
        const visible = items.slice(0, 5);
        const extra = items.length - visible.length;
        return (
            <div className="home-overdue-table-wrap">
                <table className="home-overdue-table">
                    <thead>
                        <tr>
                            <th>{t('home.table.invoiceNumber')}</th>
                            <th>{t('home.table.name')}</th>
                            <th>{t('home.table.dueDate')}</th>
                            <th className="home-overdue-table__num">{t('home.table.amount')}</th>
                            <th className="home-overdue-table__num">{t('home.table.daysOverdue')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((inv: any) => {
                            const days = daysOverdue(today, inv.paymentDueDate!);
                            const urgency = days > 90 ? 'critical' : days > 30 ? 'high' : 'medium';
                            return (
                                <tr key={inv.id}>
                                    <td><span className="home-inv-number">{inv.invoiceNumber || inv.reference || '—'}</span></td>
                                    <td className="home-overdue-table__name">{nameMap[nameKeyFn(inv)] ?? '—'}</td>
                                    <td>{inv.paymentDueDate}</td>
                                    <td className="home-overdue-table__num">{formatCurrency(inv.totalAmountIncVat)}</td>
                                    <td className="home-overdue-table__num">
                                        <span className={`home-days-badge home-days-badge--${urgency}`}>{days}d</span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {extra > 0 && (
                    <p className="home-overdue-extra">+{extra} {t('home.table.more')}</p>
                )}
            </div>
        );
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="home-screen home-screen--loading">
                <div className="spinner-border text-primary" role="status" />
                <span className="home-loading-text">{t('home.loading')}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="home-screen">
                <MainScreenHeader title={t('home.title')} />
                <div className="home-error">
                    <FaExclamationCircle className="home-error__icon" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="home-screen">
            <MainScreenHeader
                title={t('home.title')}
                subtitle={currentFY ? `${t('home.subtitle')} · ${currentFY.name}` : t('home.subtitle')}
            />

            {!currentFY && (
                <div className="home-notice">
                    <FaExclamationTriangle className="home-notice__icon" />
                    <span>{t('home.noFiscalYear')}</span>
                </div>
            )}

            <div className="home-dashboard-grid">

                {/* ── Revenue Chart ──────────────────────────────────────────── */}
                <div className="home-widget">
                    <div className="home-widget__header">
                        <span className="home-widget__title">{t('home.revenueChart.title')}</span>
                        {currentFY && (
                            <span className="home-widget__badge">{currentFY.name}</span>
                        )}
                    </div>
                    <div className="home-widget__body">
                        {revenueChartData.length === 0 ? (
                            <p className="home-no-data">{t('home.chart.noData')}</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <ComposedChart data={revenueChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6610f2" stopOpacity={0.18} />
                                            <stop offset="95%" stopColor="#6610f2" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#4e4878' }} axisLine={false} tickLine={false} />
                                    <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11, fill: '#4e4878' }} axisLine={false} tickLine={false} width={56} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                                    <Bar dataKey="monthly" name={t('home.revenueChart.monthly')} fill="#0d6efd" fillOpacity={0.75} radius={[3, 3, 0, 0]} />
                                    <Area type="monotone" dataKey="cumulative" name={t('home.revenueChart.cumulative')} stroke="#6610f2" strokeWidth={2} fill="url(#cumGrad)" dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* ── Bank Balance Chart ─────────────────────────────────────── */}
                <div className="home-widget">
                    <div className="home-widget__header">
                        <span className="home-widget__title">{t('home.bankBalanceChart.title')}</span>
                        {currentFY && (
                            <span className="home-widget__badge">{currentFY.name}</span>
                        )}
                    </div>
                    <div className="home-widget__body">
                        {bankBalanceChartData.length === 0 ? (
                            <p className="home-no-data">{t('home.chart.noData')}</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={bankBalanceChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.22} />
                                            <stop offset="95%" stopColor="#0d6efd" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#4e4878' }} axisLine={false} tickLine={false} />
                                    <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11, fill: '#4e4878' }} axisLine={false} tickLine={false} width={56} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                                    <Area type="monotone" dataKey="balance" name={t('home.bankBalanceChart.balance')} stroke="#0d6efd" strokeWidth={2} fill="url(#balGrad)" dot={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* ── Overdue Customer Payments ──────────────────────────────── */}
                <div className="home-widget">
                    <div className="home-widget__header">
                        <span className="home-widget__title">{t('home.overdueCustomers.title')}</span>
                        {overdueCustomers.length > 0 && (
                            <span className="home-widget__badge home-widget__badge--danger">{overdueCustomers.length}</span>
                        )}
                    </div>
                    <div className="home-widget__body">
                        {overdueCustomers.length === 0 ? (
                            <div className="home-all-clear">
                                <FaCheckCircle className="home-all-clear__icon" />
                                <span>{t('home.stats.noOverdue')}</span>
                            </div>
                        ) : (
                            <>
                                <div className="home-stat-row">
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--danger">{overdueCustomers.length}</span>
                                        <span className="home-stat__label">{t('home.stats.count')}</span>
                                    </div>
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--danger">{formatCurrency(overdueCustTotal)}</span>
                                        <span className="home-stat__label">{t('home.stats.totalAmount')}</span>
                                    </div>
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--muted">{overdueCustomers[0].paymentDueDate}</span>
                                        <span className="home-stat__label">{t('home.stats.oldestDue')}</span>
                                    </div>
                                </div>
                                {renderOverdueTable(
                                    overdueCustomers,
                                    customerMap,
                                    (inv: CustomerInvoice) => inv.customerId,
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* ── Overdue Supplier Payments ──────────────────────────────── */}
                <div className="home-widget">
                    <div className="home-widget__header">
                        <span className="home-widget__title">{t('home.overdueSuppliers.title')}</span>
                        {overdueSuppliers.length > 0 && (
                            <span className="home-widget__badge home-widget__badge--danger">{overdueSuppliers.length}</span>
                        )}
                    </div>
                    <div className="home-widget__body">
                        {overdueSuppliers.length === 0 ? (
                            <div className="home-all-clear">
                                <FaCheckCircle className="home-all-clear__icon" />
                                <span>{t('home.stats.noOverdue')}</span>
                            </div>
                        ) : (
                            <>
                                <div className="home-stat-row">
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--danger">{overdueSuppliers.length}</span>
                                        <span className="home-stat__label">{t('home.stats.count')}</span>
                                    </div>
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--danger">{formatCurrency(overdueSupplTotal)}</span>
                                        <span className="home-stat__label">{t('home.stats.totalAmount')}</span>
                                    </div>
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--muted">{overdueSuppliers[0].paymentDueDate}</span>
                                        <span className="home-stat__label">{t('home.stats.oldestDue')}</span>
                                    </div>
                                </div>
                                {renderOverdueTable(
                                    overdueSuppliers,
                                    supplierMap,
                                    (inv: SupplierInvoice) => inv.supplierId,
                                )}
                            </>
                        )}
                    </div>
                </div>

            </div>{/* end .home-dashboard-grid */}

            {/* ── Second row: tasks + unaccounted ─────────────────────────── */}
            <div className="home-dashboard-grid" style={{ marginTop: '0.65rem' }}>

                {/* ── Upcoming Tasks ────────────────────────────────────────── */}
                <div className="home-widget">
                    <div className="home-widget__header">
                        <FaTasks className="home-widget__header-icon" />
                        <span className="home-widget__title">{t('home.upcomingTasks.title')}</span>
                        {upcomingTasks.length > 0 && (
                            <span className="home-widget__badge">{upcomingTasks.length}</span>
                        )}
                    </div>
                    <div className="home-widget__body">
                        {upcomingTasks.length === 0 ? (
                            <div className="home-all-clear">
                                <FaCheckCircle className="home-all-clear__icon" />
                                <span>{t('home.upcomingTasks.noTasks')}</span>
                            </div>
                        ) : (
                            <>
                                <div className="home-stat-row">
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--primary">{upcomingTasks.length}</span>
                                        <span className="home-stat__label">{t('home.upcomingTasks.count')}</span>
                                    </div>
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--primary">
                                            {upcomingTasks.filter(tk => tk.dueDate && tk.dueDate < today.toISOString().slice(0, 10)).length}
                                        </span>
                                        <span className="home-stat__label">{t('home.upcomingTasks.overdue')}</span>
                                    </div>
                                </div>
                                <div className="home-overdue-table-wrap">
                                    <table className="home-overdue-table">
                                        <thead>
                                            <tr>
                                                <th>{t('home.upcomingTasks.table.title')}</th>
                                                <th>{t('home.upcomingTasks.table.priority')}</th>
                                                <th className="home-overdue-table__num">{t('home.upcomingTasks.table.dueDate')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {upcomingTasks.slice(0, 6).map(tk => {
                                                const todayStr = today.toISOString().slice(0, 10);
                                                const isOverdue = tk.dueDate && tk.dueDate < todayStr;
                                                const isDueToday = tk.dueDate === todayStr;
                                                const priorityClass = tk.priority === TaskPriority.High ? 'high' : tk.priority === TaskPriority.Medium ? 'medium' : 'low';
                                                const priorityLabel = tk.priority === TaskPriority.High
                                                    ? t('home.upcomingTasks.priority.high')
                                                    : tk.priority === TaskPriority.Medium
                                                        ? t('home.upcomingTasks.priority.medium')
                                                        : t('home.upcomingTasks.priority.low');
                                                return (
                                                    <tr key={tk.id}>
                                                        <td className="home-overdue-table__name">{tk.title}</td>
                                                        <td>
                                                            <span className={`home-priority-badge home-priority-badge--${priorityClass}`}>{priorityLabel}</span>
                                                        </td>
                                                        <td className="home-overdue-table__num">
                                                            {tk.dueDate ? (
                                                                <span className={isOverdue ? 'home-task-date--overdue' : isDueToday ? 'home-task-date--today' : undefined}>
                                                                    {isOverdue && <span className="home-days-badge home-days-badge--critical" style={{ marginRight: 4 }}>!</span>}
                                                                    {isDueToday && <span className="home-days-badge home-days-badge--medium" style={{ marginRight: 4 }}>today</span>}
                                                                    {tk.dueDate}
                                                                </span>
                                                            ) : (
                                                                <span className="home-task-date--none">{t('home.upcomingTasks.noDueDate')}</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {upcomingTasks.length > 6 && (
                                        <p className="home-overdue-extra">+{upcomingTasks.length - 6} {t('home.upcomingTasks.more')}</p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Unaccounted Bank Transactions ─────────────────────────── */}
                <div className="home-widget">
                    <div className="home-widget__header">
                        <FaUniversity className="home-widget__header-icon" />
                        <span className="home-widget__title">{t('home.unaccountedTransactions.title')}</span>
                        {unaccountedTransactions.length > 0 && (
                            <span className="home-widget__badge home-widget__badge--danger">{unaccountedTransactions.length}</span>
                        )}
                    </div>
                    <div className="home-widget__body">
                        {unaccountedTransactions.length === 0 ? (
                            <div className="home-all-clear">
                                <FaCheckCircle className="home-all-clear__icon" />
                                <span>{t('home.unaccountedTransactions.noItems')}</span>
                            </div>
                        ) : (
                            <>
                                <div className="home-stat-row">
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--danger">{unaccountedTransactions.length}</span>
                                        <span className="home-stat__label">{t('home.unaccountedTransactions.count')}</span>
                                    </div>
                                    <div className="home-stat">
                                        <span className="home-stat__value home-stat__value--danger">{formatCurrency(Math.abs(unaccountedTotal))}</span>
                                        <span className="home-stat__label">{t('home.unaccountedTransactions.totalValue')}</span>
                                    </div>
                                </div>
                                <div className="home-overdue-table-wrap">
                                    <table className="home-overdue-table">
                                        <thead>
                                            <tr>
                                                <th>{t('home.unaccountedTransactions.table.date')}</th>
                                                <th>{t('home.unaccountedTransactions.table.counterparty')}</th>
                                                <th className="home-overdue-table__num">{t('home.unaccountedTransactions.table.amount')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {unaccountedTransactions.slice(0, 6).map(tx => {
                                                const ind = (tx.creditDebitIndicator ?? '').toUpperCase();
                                                const signed = (ind === 'C' || ind === 'RD' ? 1 : -1) * tx.amount;
                                                return (
                                                    <tr key={tx.id}>
                                                        <td style={{ whiteSpace: 'nowrap' }}>{tx.valueDate}</td>
                                                        <td className="home-overdue-table__name">
                                                            {tx.narrativeCounterpartyName || tx.customerReference || '—'}
                                                        </td>
                                                        <td className="home-overdue-table__num" style={{ color: signed >= 0 ? '#198754' : '#dc3545' }}>
                                                            {formatCurrency(signed)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {unaccountedTransactions.length > 6 && (
                                        <p className="home-overdue-extra">+{unaccountedTransactions.length - 6} {t('home.unaccountedTransactions.more')}</p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

            </div>{/* end second row */}
        </div>
    );
};

export default HomeScreen;

